import {makeImageReference} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {sendEvent} from '@github-ui/hydro-analytics'

import type {CopilotAutocompleteManager} from './copilot-autocompletions'
import type {CopilotChatManager} from './copilot-chat-manager'
import type {CopilotChatState} from './copilot-chat-reducer'
import type {CopilotChatModel} from './copilot-chat-types'

// Should be limited to images that we know we can base64 encode and are supported by our models.
// We pull the types from the model capabilities and filter for these types.
const ACCEPTED_BASE64_IMAGE_FILE_MIME_TYPES: string[] = ['image/gif', 'image/jpeg', 'image/png', 'image/webp']

export class CopilotImageAttacher {
  private state: CopilotChatState
  private manager: CopilotChatManager
  private autocomplete: CopilotAutocompleteManager

  public constructor(state: CopilotChatState, manager: CopilotChatManager, autocomplete: CopilotAutocompleteManager) {
    this.state = state
    this.manager = manager
    this.autocomplete = autocomplete
  }

  public static getAllowedImageFileExtensions(model: CopilotChatModel): string {
    return this.getFileExtensions(model).join(',')
  }

  public static isTypeAllowed(itemType: string, model: CopilotChatModel): boolean {
    itemType = itemType.toLowerCase()
    const mimeTypes = this.getMimeTypes(model)
    return mimeTypes.includes(itemType)
  }

  private static getFileExtensions(model: CopilotChatModel): string[] {
    const extensions = this.getMimeTypes(model).map(type => `.${type.split('/')[1]}`)

    if (extensions.includes('.jpeg')) {
      extensions.push('.jpg')
    }

    return extensions
  }

  private static getMimeTypes(model: CopilotChatModel): string[] {
    if (model.capabilities.limits.vision) {
      const supportedTypes = model.capabilities.limits.vision.supported_media_types
      // for now, we can only return supported types that can be base 64 encoded. Remove once we stop
      // encoding images as base 64
      return supportedTypes.filter(type => ACCEPTED_BASE64_IMAGE_FILE_MIME_TYPES.includes(type))
    }

    return []
  }

  public async attachImageToChatInput(
    file: File,
    // This image element is expected to be used for loading the image and performing any operations on it that we want.
    // The image should not be visible to the user.
    imageElementForLoading: HTMLImageElement | null,
  ): Promise<void> {
    // If imageElementForLoading is null, we panic. This isn't expected, but the nature of ref loading means this is a possible
    // outcome according to types, so we will just guard up here to make sure it shows up in sentry.
    if (!imageElementForLoading) {
      const errorMessage = "Image element wasn't properly loaded before calling attachImage."
      sendEvent('dotcom_chat.vision.error', {type: 'image_loading_element_null'})
      throw new Error(errorMessage)
    }

    return new Promise<void>((resolve, reject) => {
      const reader = new FileReader()

      // error here if there is already one image uploaded
      const hasImageReference = this.state.currentReferences.some(ref => ref.type === 'image')
      if (hasImageReference) {
        const errorMessage = 'Only one image can be uploaded at a time'
        this.manager.addAmbientError(errorMessage)
        sendEvent('dotcom_chat.vision.error', {type: 'file_limit_reached'})
        reject(new Error(errorMessage))
      }

      const addImageReference = async (
        name: string,
        image: string,
        width: number | undefined,
        height: number | undefined,
      ) => {
        const imageRef = makeImageReference(name, image, width, height)
        await this.autocomplete.addToReferences(imageRef, this.state)
      }

      reader.onload = loadedFileEvent => {
        // This executes asynchronously after the call to readAsDataURL.
        if (!loadedFileEvent.target || !loadedFileEvent.target.result) return
        const result = loadedFileEvent.target.result
        if (typeof result !== 'string') return
        const base64String = result.split(',')[1] // Strip the data URL prefix
        if (base64String) {
          if (base64String.length > 500_000) {
            const errorMessage = 'Only images below 500KB are supported during this preview period'
            this.manager.addAmbientError(errorMessage)
            sendEvent('dotcom_chat.vision.error', {type: 'size_limit_exceeded', size: base64String.length})
            reject(new Error(errorMessage))
            return
          }

          const imageReferenceAdder = (width: number | undefined, height: number | undefined) => {
            // eslint-disable-next-line github/no-then
            addImageReference(file.name, result, width, height).then(
              () => {
                resolve()
              },
              error => {
                if (typeof error === 'string') {
                  reject(new Error(error))
                } else if (error instanceof Error) {
                  reject(error)
                } else {
                  reject(new Error('Unknown error occurred while trying to add image reference.'))
                }
              },
            )
          }

          imageElementForLoading.onload = () => {
            const foundWidth = imageElementForLoading.width
            const foundHeight = imageElementForLoading.height
            imageReferenceAdder(foundWidth, foundHeight)
          }
          // Note that this is using result, not base64String, because result is the full data URL
          imageElementForLoading.src = result
        } else {
          reject(new Error("Couldn't read the file as a Base64 string"))
        }
      }

      reader.onerror = fileErrorEvent => {
        // This executes asynchronously after the call to readAsDataURL.
        if (fileErrorEvent.target) {
          const error = fileErrorEvent.target.error
          if (error) {
            // since this is kicking off as a staff ship, it should be safe to expose the error name and message
            this.manager.addAmbientError(`An error occurred while reading the file: ${error.name} - ${error.message}`)
            sendEvent('dotcom_chat.vision.error', {type: error.name, message: error.message})
            reject(error)
            return
          }
        }
        reject(new Error("Couldn't read the file due to an unknown error"))
      }

      reader.readAsDataURL(file) // Reads the file as a data URL, which includes the Base64-encoded string
    })
  }
}
